import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _isEmpty from "lodash/isEmpty";
import _isNumber from "lodash/isNumber";

var _params;

import "core-js/modules/es6.object.keys.js";
import "core-js/modules/es6.number.constructor.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.regexp.to-string.js";
import React from 'react';
import PropTypes from 'prop-types';
import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import Type from '@nerdwallet/react-typography';
import { Box } from '@nerdwallet/currency';
import Popover from '@nerdwallet/react-popover';
import { colors } from '@nerdwallet/base-styles/variables';
import { Tracking } from '@nerdwallet/nw-client-lib';
import { setTrk, TrkTag, TrkElement } from '@nerdwallet/nw-client-lib/trk-helper';
import { Impression } from '@nerdwallet/analytics/react/impression';
import gradientStringBuilder from './utils';
import * as styles from '../approval-odds.module.less';
var LAYOUT = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
};
export var AO_COLORS = {
  DEFAULT: colors['neutral-lighter'],
  DEFAULT_BACKGROUND: colors['neutral-lightest'],
  LOW: colors['red-light'],
  MEDIUM: colors.yellow,
  HIGH: colors['green-dark']
};
export var AO_RANGES = {
  LOW: {
    min: 0,
    max: 0.45
  },
  MEDIUM: {
    min: 0.45,
    max: 0.65
  },
  HIGH: {
    min: 0.65,
    max: 1
  }
};
export var CREDIT_DASHBOARD_PATH = '/home/dashboard/credit-score';
export var CREDIT_DASHBOARD_PATH_WITH_TRK = setTrk({
  url: CREDIT_DASHBOARD_PATH,
  params: (_params = {}, _defineProperty(_params, TrkTag.element, TrkElement.hyperlink), _defineProperty(_params, TrkTag.location, 'approval_odds'), _params)
}); // TODO: when AO component has been launched, move this logic to nw-client-lib utils for sharing
// see https://github.com/NerdWallet/react-components/pull/1041#discussion_r219310105

export function calculateBucket(approvalOdds) {
  if (approvalOdds >= 0.05 && approvalOdds < 0.15) {
    return {
      percentile: 10,
      numSegments: 0.5
    };
  } else if (approvalOdds >= 0.15 && approvalOdds < 0.25) {
    return {
      percentile: 20,
      numSegments: 1
    };
  } else if (approvalOdds >= 0.25 && approvalOdds < 0.35) {
    return {
      percentile: 30,
      numSegments: 1.5
    };
  } else if (approvalOdds >= 0.35 && approvalOdds < 0.45) {
    return {
      percentile: 40,
      numSegments: 2
    };
  } else if (approvalOdds >= 0.45 && approvalOdds < 0.55) {
    return {
      percentile: 50,
      numSegments: 2.5
    };
  } else if (approvalOdds >= 0.55 && approvalOdds < 0.65) {
    return {
      percentile: 60,
      numSegments: 3
    };
  } else if (approvalOdds >= 0.65 && approvalOdds < 0.75) {
    return {
      percentile: 70,
      numSegments: 3.5
    };
  } else if (approvalOdds >= 0.75 && approvalOdds < 0.85) {
    return {
      percentile: 80,
      numSegments: 4
    };
  } else if (approvalOdds >= 0.85) {
    return {
      percentile: 90,
      numSegments: 4.5
    };
  }

  return {
    percentile: 0,
    numSegments: 0
  };
} //

/**
 * Returns color of approval bar and labels
 *
 * @param {object} props
 */

export var getColorAndLabel = function getColorAndLabel(props) {
  var approvalOdds = props.approvalOdds;

  if (approvalOdds < AO_RANGES.LOW.max) {
    return {
      color: AO_COLORS.LOW,
      label: 'poor'
    };
  } else if (approvalOdds >= AO_RANGES.MEDIUM.min && approvalOdds < AO_RANGES.MEDIUM.max) {
    return {
      color: AO_COLORS.MEDIUM,
      label: 'good'
    };
  } else if (approvalOdds >= AO_RANGES.HIGH.min) {
    return {
      color: AO_COLORS.HIGH,
      label: 'excellent'
    };
  }

  return {
    color: AO_COLORS.DEFAULT,
    label: ''
  };
};
/**
 * Returns the popover content for approval odds.
 *
 * @param {number} approvalOdds
 * @param {object} styles
 */

export var approvalOddsPopoverContent = function approvalOddsPopoverContent(approvalOdds) {
  return /*#__PURE__*/React.createElement(Box, {
    className: styles.popoverContent
  }, /*#__PURE__*/React.createElement(Type, {
    size: 1
  }, "We think your odds of getting approved for this card are", /*#__PURE__*/React.createElement(Type, {
    size: 1,
    bold: true,
    className: styles.popoverOdds
  }, "\xA0", getColorAndLabel({
    approvalOdds: approvalOdds
  }).label, "\xA0"), "based on your credit score and other factors. That said, these odds are an estimate to help you narrow your choices, not a guarantee. Card issuers may use different criteria than we do."));
}; // Require tracking props if useTracking is true
// needs to not return anything if the prop is valid
// eslint-disable-next-line consistent-return

var validateTrackingProps = function validateTrackingProps(props, propName) {
  if (props.useTracking) {
    if (props[propName] === undefined || typeof props[propName] !== 'string' && typeof props[propName] !== 'number') {
      return new Error("".concat(propName, " is required when useTracking is true. Please provide a valid prop."));
    }
  }
};

var CCElementImpression = Tracking.CCElementImpressionConnect;
var CCElementInteraction = Tracking.CCElementInteractionConnect;
var colorsArray = Object.keys(colors);

var ApprovalOdds = /*#__PURE__*/function (_React$Component) {
  _inherits(ApprovalOdds, _React$Component);

  var _super = _createSuper(ApprovalOdds);

  function ApprovalOdds() {
    var _this;

    _classCallCheck(this, ApprovalOdds);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "createApprovalBar", function () {
      var backgroundColor = colors[_this.props.backgroundColor];

      var _getColorAndLabel = getColorAndLabel(_this.props),
          color = _getColorAndLabel.color;

      var _calculateBucket = calculateBucket(_this.props.approvalOdds),
          numSegments = _calculateBucket.numSegments,
          percentile = _calculateBucket.percentile;

      var defaultBarColor = colors['neutral-lighter'];
      var colorBarStyles = {
        borderLeft: "2px solid ".concat(backgroundColor),
        borderRight: "2px solid ".concat(backgroundColor)
      };
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        style: _objectSpread(_objectSpread({}, colorBarStyles), {}, {
          background: gradientStringBuilder(defaultBarColor, backgroundColor),
          width: '100%'
        }),
        className: classNames('approval-odds-bar__background', styles.bar)
      }), /*#__PURE__*/React.createElement("div", {
        className: classNames('approval-odds-bar__color', styles.bar),
        style: _objectSpread(_objectSpread({}, colorBarStyles), {}, {
          background: gradientStringBuilder(color, backgroundColor, numSegments),
          width: "".concat(percentile, "%")
        })
      }));
    });

    return _this;
  }

  _createClass(ApprovalOdds, [{
    key: "calculateMarkerPosition",
    value: // Returns the css for the left offset percent of the credit score marker
    function calculateMarkerPosition(shouldOffsetMarker) {
      var bucket = calculateBucket(this.props.approvalOdds).percentile;

      if (bucket === 0) {
        return '0%';
      } else if (shouldOffsetMarker && bucket % 20 === 0) {
        // marker appears at the end of a segment, remove some padding to show space
        return "calc(".concat(bucket, "% - 11px)");
      } // marker appears within a segment - remove some padding so marker completely covers end of colored line


      return "calc(".concat(bucket, "% - 1px)");
    } // Returns the markup for the text below the approval bar

  }, {
    key: "render",
    value: // eslint-disable-next-line react/sort-comp
    function render() {
      var _this2 = this;

      var _this$props = this.props,
          analytics = _this$props.analytics,
          approvalOdds = _this$props.approvalOdds,
          cardId = _this$props.cardId,
          id = _this$props.id,
          impressionKey = _this$props.impressionKey,
          layout = _this$props.layout,
          modelExecutionIds = _this$props.modelExecutionIds,
          sectionCategory = _this$props.sectionCategory,
          sectionPosition = _this$props.sectionPosition,
          useTracking = _this$props.useTracking,
          width = _this$props.width; // handle null case

      if (!_isNumber(approvalOdds)) {
        return false;
      }

      var _getColorAndLabel2 = getColorAndLabel(this.props),
          color = _getColorAndLabel2.color,
          label = _getColorAndLabel2.label;

      var impressionFields = {
        id: "".concat(cardId, "-approval-odds"),
        elementDomId: Number(cardId),
        elementName: 'Approval Odds',
        elementCategory: label.toUpperCase(),
        elementContent: approvalOdds.toString(),
        modelExecutionIds: modelExecutionIds,
        sectionCategory: sectionCategory,
        sectionPosition: sectionPosition,
        productId: cardId,
        productImpressionKey: impressionKey
      };
      var elementId = "".concat(this.props.id, "__marker");
      var markerOffset = this.calculateMarkerPosition(true);
      var markerStyle = {
        marginLeft: markerOffset,
        backgroundColor: color
      };
      var contentStyle = {
        maxWidth: layout === LAYOUT.HORIZONTAL ? width : null
      };

      var barMarkup = function barMarkup() {
        var markup = /*#__PURE__*/React.createElement("div", {
          className: classNames(styles.top, classes(['width-100'])),
          style: contentStyle,
          key: cardId
        }, _this2.createApprovalBar(), /*#__PURE__*/React.createElement(Type, {
          className: classNames(styles.marker, 'approval-odds-marker'),
          id: elementId,
          size: 1,
          style: markerStyle
        }, label));

        if (useTracking) {
          return /*#__PURE__*/React.createElement(CCElementInteraction, _extends({}, impressionFields, {
            type: "HOVER"
          }), markup);
        }

        return markup;
      };

      var aoComponent = /*#__PURE__*/React.createElement(Impression, {
        "aria-label": "Your approval odds for this card are ".concat(label, "."),
        className: "approval-odds",
        entityName: "cc_approval_odds",
        eventType: "approval_odds_element_impression",
        payload: {
          section_name: analytics === null || analytics === void 0 ? void 0 : analytics.sectionName,
          section_position: analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition,
          additional_attributes: {
            approval_odds: label
          }
        },
        enabled: !_isEmpty(analytics)
      }, /*#__PURE__*/React.createElement("div", {
        className: classNames(styles["layout".concat(layout)], classes(['display-flex', 'flex-wrap', 'flex-direction-column', // Force column layout on small viewports
        'flex-direction-row--wide', 'align-items-center--wide', 'padding-vertical-3', 'width-100', {
          'justify-content-space-between--wide': layout === LAYOUT.HORIZONTAL
        }]))
      }, /*#__PURE__*/React.createElement(Popover, {
        id: "".concat(id, "__popover"),
        type: ['click', 'hover'],
        placement: "top",
        popoverClassName: styles.popoverContainer,
        popoverContent: approvalOddsPopoverContent(this.props.approvalOdds),
        className: classNames('ao-popover-icon', // for ease of unit testing
        classes(['display-block', 'width-100']))
      }, barMarkup())));

      if (useTracking) {
        return /*#__PURE__*/React.createElement(CCElementImpression, impressionFields, aoComponent);
      }

      return aoComponent;
    }
  }]);

  return ApprovalOdds;
}(React.Component);

_defineProperty(ApprovalOdds, "colorsArray", colorsArray);

_defineProperty(ApprovalOdds, "propTypes", {
  analytics: PropTypes.shape({
    sectionName: PropTypes.string,
    sectionPosition: PropTypes.string
  }),

  /** Number between 0 and 1, supplied by the Offer API */
  approvalOdds: PropTypes.number.isRequired,

  /** ID of the card offer for which the approval odds has been rendered. */
  cardId: validateTrackingProps,

  /** Unique id string for the component */
  id: PropTypes.string.isRequired,

  /**
   * Background color of the container in which the component appears.
   * Allows for spaces between segments to appear transparent.
   */
  backgroundColor: PropTypes.oneOf(colorsArray),

  /** CC-specific tracking identifier, required if useTracking is true */
  impressionKey: validateTrackingProps,

  /** With the width prop, determines the width of the component */
  layout: PropTypes.oneOf([LAYOUT.HORIZONTAL, LAYOUT.VERTICAL]),

  /** Tracking information, required if useTracking is true */
  sectionCategory: validateTrackingProps,

  /** Tracking information, required if useTracking is true */
  sectionPosition: validateTrackingProps,

  /** Tracking information, optional */
  modelExecutionIds: PropTypes.arrayOf(PropTypes.string),

  /**
   * Boolean to send tracking information. If true, then supply
   * impressionKey, sectionCategory, sectionPosition props.
   */
  useTracking: PropTypes.bool,

  /** Define a fixed width. */
  width: PropTypes.number
});

_defineProperty(ApprovalOdds, "defaultProps", {
  analytics: {},
  backgroundColor: 'neutral-lightest',
  cardId: null,
  layout: 'vertical',
  sectionCategory: '',
  sectionPosition: '',
  impressionKey: null,
  modelExecutionIds: [],
  useTracking: false,
  width: null
});

export default ApprovalOdds;